import { useEffect, useState } from 'react';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useModal } from '@/hooks';
import { usePatchSessionDisplayName } from '@/services';
import { Button } from '@/components/ui';

const DEFAULT_SESSION_DISPLAY_NAME = 'Sessão';

const labelClass =
	'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';

function persistedDisplayName(session: Sessions.Session): string {
	const raw = session.display_name;
	if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
	return '';
}

const EditSessionDisplayNameModal = ({
	session,
}: {
	session: Sessions.Session;
}) => {
	const { closeModal } = useModal();
	const [value, setValue] = useState(() => persistedDisplayName(session));

	useEffect(() => {
		setValue(persistedDisplayName(session));
	}, [session.id, session.display_name]);

	const { mutate, isPending } = usePatchSessionDisplayName(() => {
		closeModal();
	});

	const trimmed = value.trim();
	const isValid = trimmed.length > 0;
	const serverName = persistedDisplayName(session);
	const unchanged = isValid && trimmed === serverName;

	const inputBaseClass =
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

	const inputClass = cn(
		inputBaseClass,
		!isValid
			? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
			: 'border-stone-200 focus:border-stone-400 focus:bg-white',
	);

	const handleSave = () => {
		if (!isValid || isPending || unchanged) return;
		mutate({ id: session.id, body: { display_name: trimmed } });
	};

	const handleRestoreDefault = () => {
		if (isPending) return;
		setValue(DEFAULT_SESSION_DISPLAY_NAME);
	};

	return (
		<div className="space-y-6">
			<div className="box-border w-full space-y-6">
				<div className="w-full min-w-0">
					<label
						htmlFor="session-display-name-input"
						className={labelClass}
					>
						NOME DA SESSÃO
					</label>
					<input
						id="session-display-name-input"
						type="text"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						maxLength={255}
						disabled={isPending}
						autoComplete="off"
						placeholder="Ex.: Consulta de retorno"
						className={cn(inputClass, 'mt-2')}
					/>
					<p className="ml-1 mt-1 text-xs font-medium leading-relaxed text-stone-400">
						Esse nome aparece para paciente e profissional. Não pode
						ficar em branco.
					</p>
					{!isValid && (
						<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
							<AlertCircle size={10} /> Digite pelo menos um
							caractere visível.
						</span>
					)}
				</div>

				<div className="mt-8 flex w-full shrink-0 flex-wrap items-center justify-between gap-4 border-t border-stone-50 pt-8">
					<button
						type="button"
						onClick={handleRestoreDefault}
						disabled={isPending}
						className="shrink-0 rounded-full border border-zinc-300 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						Restaurar padrão
					</button>
					<div className="flex flex-wrap items-center justify-end gap-6">
						<button
							type="button"
							onClick={closeModal}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							Cancelar
						</button>
						<Button
							type="button"
							text={isPending ? 'Salvando...' : 'Salvar'}
							disabled={!isValid || isPending || unchanged}
							onClick={handleSave}
							className="min-w-[140px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800 disabled:opacity-50"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export { EditSessionDisplayNameModal };
